import { useState } from "react";
import {
	Box,
	Typography,
	Button,
	TextField,
	Card,
	CardContent,
	CardHeader,
	CircularProgress,
	Alert,
	Divider,
	Grid,
	Chip,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	TablePagination,
	IconButton,
	Tooltip,
	InputAdornment,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SearchIcon from "@mui/icons-material/Search";
import StorageIcon from "@mui/icons-material/Storage";
import PetsIcon from "@mui/icons-material/Pets";
import PeopleIcon from "@mui/icons-material/People";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useSnackbar } from "notistack";
import Page from "../../components/Page";
import axiosInstance from "../../utils/axios";

export default function VetDataTest() {
	const { enqueueSnackbar } = useSnackbar();

	const [installationLoading, setInstallationLoading] = useState(false);
	const [installationData, setInstallationData] = useState(null);
	const [installationError, setInstallationError] = useState(null);

	const [installationId, setInstallationId] = useState("b43015a2-eedb-4da3-9c1a-7968fbf71900");
	const [patientId, setPatientId] = useState("2126067");
	const [patientLoading, setPatientLoading] = useState(false);
	const [patientData, setPatientData] = useState(null);
	const [patientError, setPatientError] = useState(null);

	const [installationPage, setInstallationPage] = useState(0);
	const [installationRowsPerPage, setInstallationRowsPerPage] = useState(10);

	const [expandedRow, setExpandedRow] = useState(null);
	const [installationSearch, setInstallationSearch] = useState("");

	const [clientId, setClientId] = useState("");
	const [clientLoading, setClientLoading] = useState(false);
	const [clientData, setClientData] = useState(null);
	const [clientError, setClientError] = useState(null);
	const [clientPage, setClientPage] = useState(0);
	const [clientRowsPerPage, setClientRowsPerPage] = useState(10);
	const [clientSearch, setClientSearch] = useState("");
	const [expandedClientRow, setExpandedClientRow] = useState(null);

	const fetchInstallations = async () => {
		setInstallationLoading(true);
		setInstallationError(null);
		setInstallationData(null);
		try {
			const response = await axiosInstance.get("covetrus/installations");
			setInstallationData(response.data);
		} catch (error) {
			const msg =
				error?.response?.data?.message ||
				error?.message ||
				"Failed to fetch installations";
			setInstallationError(msg);
			enqueueSnackbar(msg, { variant: "error" });
		} finally {
			setInstallationLoading(false);
		}
	};

	const fetchPatient = async () => {
		if (!patientId.trim()) {
			enqueueSnackbar("Please enter a Patient ID", { variant: "warning" });
			return;
		}
		if (!installationId.trim()) {
			enqueueSnackbar("Please enter an Installation ID", { variant: "warning" });
			return;
		}
		setPatientLoading(true);
		setPatientError(null);
		setPatientData(null);
		try {
			const response = await axiosInstance.get(
				`covetrus/patients/${patientId.trim()}`,
				{ params: { installationId: installationId.trim() } }
			);
			setPatientData(response.data);
		} catch (error) {
			const msg =
				error?.response?.data?.message ||
				error?.message ||
				"Failed to fetch patient";
			setPatientError(msg);
			enqueueSnackbar(msg, { variant: "error" });
		} finally {
			setPatientLoading(false);
		}
	};

	const extractErrorInfo = (error, fallback) => {
		const payload = error?.response?.data ?? error?.data ?? error;
		const msg =
			(typeof payload === "string" && payload) ||
			payload?.message ||
			error?.message ||
			fallback;
		const details = payload?.details ?? null;
		return { msg, details };
	};

	const fetchAllClients = async () => {
		if (!installationId.trim()) {
			enqueueSnackbar("Please enter an Installation ID", { variant: "warning" });
			return;
		}
		setClientLoading(true);
		setClientError(null);
		setClientData(null);
		setClientPage(0);
		setClientSearch("");
		setExpandedClientRow(null);
		try {
			const response = await axiosInstance.get("covetrus/clients", {
				params: { installationId: installationId.trim() },
			});
			setClientData(response.data);
		} catch (error) {
			const { msg, details } = extractErrorInfo(error, "Failed to fetch clients");
			setClientError({ msg, details });
			enqueueSnackbar(msg, { variant: "error" });
		} finally {
			setClientLoading(false);
		}
	};

	const fetchClientById = async () => {
		if (!clientId.trim()) {
			enqueueSnackbar("Please enter a Client ID", { variant: "warning" });
			return;
		}
		if (!installationId.trim()) {
			enqueueSnackbar("Please enter an Installation ID", { variant: "warning" });
			return;
		}
		setClientLoading(true);
		setClientError(null);
		setClientData(null);
		setClientPage(0);
		setClientSearch("");
		setExpandedClientRow(null);
		try {
			const response = await axiosInstance.get(
				`covetrus/clients/${clientId.trim()}`,
				{ params: { installationId: installationId.trim() } }
			);
			setClientData(response.data);
		} catch (error) {
			const { msg, details } = extractErrorInfo(error, "Failed to fetch client");
			setClientError({ msg, details });
			enqueueSnackbar(msg, { variant: "error" });
		} finally {
			setClientLoading(false);
		}
	};

	const copyToClipboard = (text) => {
		navigator.clipboard.writeText(JSON.stringify(text, null, 2)).then(() => {
			enqueueSnackbar("Copied to clipboard", { variant: "success" });
		});
	};

	const renderInstallationTable = () => {
		if (!installationData) return null;

		const allItems = Array.isArray(installationData)
			? installationData
			: installationData.value || installationData.results || [];
		const totalCount =
			installationData["odata.count"] ||
			installationData["@odata.count"] ||
			allItems.length;

		if (allItems.length === 0) {
			return (
				<Alert severity="info" sx={{ mt: 2 }}>
					No installations found.
				</Alert>
			);
		}

		const query = installationSearch.toLowerCase().trim();
		const items = query
			? allItems.filter((item) => {
					const searchable = [
						item.PracticeName,
						item.PartnerIdentifier,
						item.PMS,
						item.InstallationId,
					]
						.filter(Boolean)
						.join(" ")
						.toLowerCase();
					return searchable.includes(query);
			  })
			: allItems;

		const preferredColumns = ["InstallationId", "PartnerIdentifier", "PMS", "PracticeName"];
		const allKeys = Object.keys(allItems[0] || {});
		const columns = preferredColumns.filter((c) => allKeys.includes(c));
		if (columns.length === 0) columns.push(...allKeys.slice(0, 6));
		const paginatedItems = items.slice(
			installationPage * installationRowsPerPage,
			installationPage * installationRowsPerPage + installationRowsPerPage
		);

		return (
			<Box sx={{ mt: 2 }}>
				<TextField
					fullWidth
					size="small"
					variant="outlined"
					placeholder="Search by practice name, partner ID, PMS, or installation ID..."
					value={installationSearch}
					onChange={(e) => {
						setInstallationSearch(e.target.value);
						setInstallationPage(0);
					}}
					sx={{ mb: 1.5 }}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<SearchIcon fontSize="small" />
							</InputAdornment>
						),
					}}
				/>
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						mb: 1,
					}}
				>
					<Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
						<Chip
							label={`Total: ${totalCount}`}
							color="primary"
							variant="outlined"
							size="small"
						/>
						{query && items.length !== allItems.length && (
							<Chip
								label={`Showing: ${items.length}`}
								color="secondary"
								variant="outlined"
								size="small"
							/>
						)}
					</Box>
					<Tooltip title="Copy raw JSON">
						<IconButton
							size="small"
							onClick={() => copyToClipboard(installationData)}
						>
							<ContentCopyIcon fontSize="small" />
						</IconButton>
					</Tooltip>
				</Box>
				<TableContainer
					component={Paper}
					variant="outlined"
					sx={{ maxHeight: 500 }}
				>
					<Table stickyHeader size="small">
						<TableHead>
							<TableRow>
								<TableCell sx={{ fontWeight: "bold" }}>#</TableCell>
								{columns.map((col) => (
									<TableCell key={col} sx={{ fontWeight: "bold" }}>
										{col}
									</TableCell>
								))}
								<TableCell sx={{ fontWeight: "bold" }}>Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{paginatedItems.map((item, idx) => {
								const globalIdx =
									installationPage * installationRowsPerPage + idx;
								const isExpanded = expandedRow === globalIdx;
								return (
									<>
										<TableRow
											key={globalIdx}
											hover
											sx={{ cursor: "pointer" }}
											onClick={() =>
												setExpandedRow(isExpanded ? null : globalIdx)
											}
										>
											<TableCell>{globalIdx + 1}</TableCell>
											{columns.map((col) => (
												<TableCell key={col}>
													{typeof item[col] === "object"
														? JSON.stringify(item[col])
														: String(item[col] ?? "")}
												</TableCell>
											))}
											<TableCell onClick={(e) => e.stopPropagation()}>
												<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
													{item.InstallationId && (
														<Tooltip title="Use for Patient Lookup">
															<IconButton
																size="small"
																color={installationId === item.InstallationId ? "success" : "primary"}
																onClick={() => {
																	setInstallationId(item.InstallationId);
																	enqueueSnackbar(`Installation ID set: ${item.InstallationId}`, { variant: "info" });
																}}
															>
																<CheckCircleIcon fontSize="small" />
															</IconButton>
														</Tooltip>
													)}
													<IconButton
														size="small"
														onClick={() => setExpandedRow(isExpanded ? null : globalIdx)}
													>
														{isExpanded ? (
															<ExpandLessIcon fontSize="small" />
														) : (
															<ExpandMoreIcon fontSize="small" />
														)}
													</IconButton>
												</Box>
											</TableCell>
										</TableRow>
										{isExpanded && (
											<TableRow>
												<TableCell
													colSpan={columns.length + 2}
													sx={{ bgcolor: "action.hover" }}
												>
													<Box
														component="pre"
														sx={{
															fontSize: 12,
															maxHeight: 300,
															overflow: "auto",
															m: 0,
															p: 1,
															whiteSpace: "pre-wrap",
															wordBreak: "break-word",
														}}
													>
														{JSON.stringify(item, null, 2)}
													</Box>
												</TableCell>
											</TableRow>
										)}
									</>
								);
							})}
						</TableBody>
					</Table>
				</TableContainer>
				{items.length === 0 && query ? (
					<Alert severity="info" sx={{ mt: 1 }}>
						No installations match "{installationSearch}"
					</Alert>
				) : (
					<TablePagination
						component="div"
						count={items.length}
						page={installationPage}
						onPageChange={(_, page) => setInstallationPage(page)}
						rowsPerPage={installationRowsPerPage}
						onRowsPerPageChange={(e) => {
							setInstallationRowsPerPage(parseInt(e.target.value, 10));
							setInstallationPage(0);
						}}
						rowsPerPageOptions={[5, 10, 25, 50]}
					/>
				)}
			</Box>
		);
	};

	const renderPatientData = () => {
		if (!patientData) return null;

		const items = Array.isArray(patientData)
			? patientData
			: patientData.value || patientData.results || [patientData];
		const dataArray = Array.isArray(items) ? items : [items];

		if (dataArray.length === 0) {
			return (
				<Alert severity="info" sx={{ mt: 2 }}>
					No patient found with ID: {patientId}
				</Alert>
			);
		}

		return (
			<Box sx={{ mt: 2 }}>
				<Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
					<Tooltip title="Copy raw JSON">
						<IconButton
							size="small"
							onClick={() => copyToClipboard(patientData)}
						>
							<ContentCopyIcon fontSize="small" />
						</IconButton>
					</Tooltip>
				</Box>
				{dataArray.map((patient, idx) => (
					<Card key={idx} variant="outlined" sx={{ mb: 2 }}>
						<CardContent>
							<Grid container spacing={2}>
								{Object.entries(patient).map(([key, value]) => (
									<Grid item xs={12} sm={6} md={4} key={key}>
										<Typography
											variant="caption"
											color="text.secondary"
											display="block"
										>
											{key}
										</Typography>
										<Typography variant="body2" sx={{ wordBreak: "break-word" }}>
											{typeof value === "object"
												? JSON.stringify(value, null, 2)
												: String(value ?? "—")}
										</Typography>
									</Grid>
								))}
							</Grid>
						</CardContent>
					</Card>
				))}
			</Box>
		);
	};

	const renderClientsTable = () => {
		if (!clientData) return null;

		const allItems = Array.isArray(clientData)
			? clientData
			: clientData.value || clientData.results || [clientData];
		const dataArray = Array.isArray(allItems) ? allItems : [allItems];
		const totalCount =
			clientData["odata.count"] ||
			clientData["@odata.count"] ||
			dataArray.length;

		if (dataArray.length === 0) {
			return (
				<Alert severity="info" sx={{ mt: 2 }}>
					No clients found.
				</Alert>
			);
		}

		const query = clientSearch.toLowerCase().trim();
		const items = query
			? dataArray.filter((item) => {
					const searchable = Object.values(item)
						.filter((v) => v != null && typeof v !== "object")
						.join(" ")
						.toLowerCase();
					return searchable.includes(query);
			  })
			: dataArray;

		const preferredColumns = [
			"Id",
			"FirstName",
			"LastName",
			"Email",
			"Phone",
			"PracticeName",
		];
		const allKeys = Object.keys(dataArray[0] || {});
		const columns = preferredColumns.filter((c) => allKeys.includes(c));
		if (columns.length === 0) columns.push(...allKeys.slice(0, 6));

		const paginatedItems = items.slice(
			clientPage * clientRowsPerPage,
			clientPage * clientRowsPerPage + clientRowsPerPage
		);

		return (
			<Box sx={{ mt: 2 }}>
				<TextField
					fullWidth
					size="small"
					variant="outlined"
					placeholder="Search clients..."
					value={clientSearch}
					onChange={(e) => {
						setClientSearch(e.target.value);
						setClientPage(0);
					}}
					sx={{ mb: 1.5 }}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<SearchIcon fontSize="small" />
							</InputAdornment>
						),
					}}
				/>
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						mb: 1,
					}}
				>
					<Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
						<Chip
							label={`Total: ${totalCount}`}
							color="primary"
							variant="outlined"
							size="small"
						/>
						{query && items.length !== dataArray.length && (
							<Chip
								label={`Showing: ${items.length}`}
								color="secondary"
								variant="outlined"
								size="small"
							/>
						)}
					</Box>
					<Tooltip title="Copy raw JSON">
						<IconButton
							size="small"
							onClick={() => copyToClipboard(clientData)}
						>
							<ContentCopyIcon fontSize="small" />
						</IconButton>
					</Tooltip>
				</Box>
				<TableContainer
					component={Paper}
					variant="outlined"
					sx={{ maxHeight: 500 }}
				>
					<Table stickyHeader size="small">
						<TableHead>
							<TableRow>
								<TableCell sx={{ fontWeight: "bold" }}>#</TableCell>
								{columns.map((col) => (
									<TableCell key={col} sx={{ fontWeight: "bold" }}>
										{col}
									</TableCell>
								))}
								<TableCell sx={{ fontWeight: "bold" }}>Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{paginatedItems.map((item, idx) => {
								const globalIdx = clientPage * clientRowsPerPage + idx;
								const isExpanded = expandedClientRow === globalIdx;
								return (
									<>
										<TableRow
											key={globalIdx}
											hover
											sx={{ cursor: "pointer" }}
											onClick={() =>
												setExpandedClientRow(isExpanded ? null : globalIdx)
											}
										>
											<TableCell>{globalIdx + 1}</TableCell>
											{columns.map((col) => (
												<TableCell key={col}>
													{typeof item[col] === "object"
														? JSON.stringify(item[col])
														: String(item[col] ?? "")}
												</TableCell>
											))}
											<TableCell onClick={(e) => e.stopPropagation()}>
												<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
													{item.Id && (
														<Tooltip title="Use as Client ID">
															<IconButton
																size="small"
																color={clientId === String(item.Id) ? "success" : "primary"}
																onClick={() => {
																	setClientId(String(item.Id));
																	enqueueSnackbar(`Client ID set: ${item.Id}`, { variant: "info" });
																}}
															>
																<CheckCircleIcon fontSize="small" />
															</IconButton>
														</Tooltip>
													)}
													<IconButton
														size="small"
														onClick={() =>
															setExpandedClientRow(isExpanded ? null : globalIdx)
														}
													>
														{isExpanded ? (
															<ExpandLessIcon fontSize="small" />
														) : (
															<ExpandMoreIcon fontSize="small" />
														)}
													</IconButton>
												</Box>
											</TableCell>
										</TableRow>
										{isExpanded && (
											<TableRow>
												<TableCell
													colSpan={columns.length + 2}
													sx={{ bgcolor: "action.hover" }}
												>
													<Box
														component="pre"
														sx={{
															fontSize: 12,
															maxHeight: 300,
															overflow: "auto",
															m: 0,
															p: 1,
															whiteSpace: "pre-wrap",
															wordBreak: "break-word",
														}}
													>
														{JSON.stringify(item, null, 2)}
													</Box>
												</TableCell>
											</TableRow>
										)}
									</>
								);
							})}
						</TableBody>
					</Table>
				</TableContainer>
				{items.length === 0 && query ? (
					<Alert severity="info" sx={{ mt: 1 }}>
						No clients match "{clientSearch}"
					</Alert>
				) : (
					<TablePagination
						component="div"
						count={items.length}
						page={clientPage}
						onPageChange={(_, page) => setClientPage(page)}
						rowsPerPage={clientRowsPerPage}
						onRowsPerPageChange={(e) => {
							setClientRowsPerPage(parseInt(e.target.value, 10));
							setClientPage(0);
						}}
						rowsPerPageOptions={[5, 10, 25, 50]}
					/>
				)}
			</Box>
		);
	};

	return (
		<Page title="VetData API Test">
			<Box sx={{ p: 3 }}>
				<Typography variant="h4" gutterBottom>
					VetData API Endpoints
				</Typography>
				<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
					Test Covetrus VetData API endpoints. Tokens are automatically managed
					on the server side.
				</Typography>

				<Grid container spacing={3}>
					{/* Installation List */}
					<Grid item xs={12}>
						<Card>
							<CardHeader
								avatar={<StorageIcon color="primary" />}
								title="Installation List"
								subheader="GET /InstallationList?$inlinecount=allpages"
								action={
									<Button
										variant="contained"
										startIcon={
											installationLoading ? (
												<CircularProgress size={18} color="inherit" />
											) : (
												<PlayArrowIcon />
											)
										}
										onClick={fetchInstallations}
										disabled={installationLoading}
									>
										{installationLoading ? "Fetching..." : "Run"}
									</Button>
								}
							/>
							<Divider />
							<CardContent>
								<Typography variant="body2" color="text.secondary">
									Fetches all installations from the VetData API with inline
									count.
								</Typography>

								{installationError && (
									<Alert severity="error" sx={{ mt: 2 }}>
										{installationError}
									</Alert>
								)}

								{renderInstallationTable()}
							</CardContent>
						</Card>
					</Grid>

					{/* Patient Lookup */}
					<Grid item xs={12}>
						<Card>
							<CardHeader
								avatar={<PetsIcon color="primary" />}
								title="Patient Lookup"
								subheader="GET /v2/Patients?$filter=Id eq '{id}' (requires Installation header)"
							/>
							<Divider />
							<CardContent>
								<Box
									sx={{
										display: "flex",
										gap: 2,
										alignItems: "flex-start",
										flexWrap: "wrap",
										mb: 1,
									}}
								>
									<TextField
										label="Installation ID"
										variant="outlined"
										size="small"
										value={installationId}
										onChange={(e) => setInstallationId(e.target.value)}
										sx={{ width: 420 }}
										helperText="Required — select from Installation List above"
										InputProps={{
											startAdornment: (
												<InputAdornment position="start">
													<StorageIcon fontSize="small" />
												</InputAdornment>
											),
										}}
									/>
									<TextField
										label="Patient ID"
										variant="outlined"
										size="small"
										value={patientId}
										onChange={(e) => setPatientId(e.target.value)}
										onKeyDown={(e) => e.key === "Enter" && fetchPatient()}
										sx={{ width: 300 }}
										InputProps={{
											startAdornment: (
												<InputAdornment position="start">
													<SearchIcon fontSize="small" />
												</InputAdornment>
											),
										}}
									/>
									<Button
										variant="contained"
										startIcon={
											patientLoading ? (
												<CircularProgress size={18} color="inherit" />
											) : (
												<PlayArrowIcon />
											)
										}
										onClick={fetchPatient}
										disabled={patientLoading || !patientId.trim() || !installationId.trim()}
										sx={{ mt: "2px" }}
									>
										{patientLoading ? "Fetching..." : "Run"}
									</Button>
								</Box>

								{patientError && (
									<Alert severity="error" sx={{ mt: 2 }}>
										{patientError}
									</Alert>
								)}

								{renderPatientData()}
							</CardContent>
						</Card>
					</Grid>

					{/* Clients */}
					<Grid item xs={12}>
						<Card>
							<CardHeader
								avatar={<PeopleIcon color="primary" />}
								title="Clients"
								subheader="GET /v2/Clients (requires Installation header) — list all or filter by Client ID"
							/>
							<Divider />
							<CardContent>
								<Box
									sx={{
										display: "flex",
										gap: 2,
										alignItems: "flex-start",
										flexWrap: "wrap",
										mb: 1,
									}}
								>
									<TextField
										label="Installation ID"
										variant="outlined"
										size="small"
										value={installationId}
										onChange={(e) => setInstallationId(e.target.value)}
										sx={{ width: 420 }}
										helperText="Required — select from Installation List above"
										InputProps={{
											startAdornment: (
												<InputAdornment position="start">
													<StorageIcon fontSize="small" />
												</InputAdornment>
											),
										}}
									/>
									<TextField
										label="Client ID (optional)"
										variant="outlined"
										size="small"
										value={clientId}
										onChange={(e) => setClientId(e.target.value)}
										onKeyDown={(e) => e.key === "Enter" && fetchClientById()}
										sx={{ width: 300 }}
										helperText="Leave empty to fetch all clients"
										InputProps={{
											startAdornment: (
												<InputAdornment position="start">
													<SearchIcon fontSize="small" />
												</InputAdornment>
											),
										}}
									/>
									<Box sx={{ display: "flex", gap: 1, mt: "2px" }}>
										<Button
											variant="contained"
											startIcon={
												clientLoading ? (
													<CircularProgress size={18} color="inherit" />
												) : (
													<PlayArrowIcon />
												)
											}
											onClick={fetchAllClients}
											disabled={clientLoading || !installationId.trim()}
										>
											{clientLoading ? "Fetching..." : "Get All"}
										</Button>
										<Button
											variant="outlined"
											startIcon={
												clientLoading ? (
													<CircularProgress size={18} />
												) : (
													<SearchIcon />
												)
											}
											onClick={fetchClientById}
											disabled={
												clientLoading ||
												!clientId.trim() ||
												!installationId.trim()
											}
										>
											Get By ID
										</Button>
									</Box>
								</Box>

								{clientError && (
									<Alert severity="error" sx={{ mt: 2 }}>
										<Typography variant="body2" sx={{ fontWeight: 600 }}>
											{clientError.msg}
										</Typography>
										{clientError.details != null && (
											<Box
												component="pre"
												sx={{
													fontSize: 11,
													maxHeight: 240,
													overflow: "auto",
													m: 0,
													mt: 1,
													p: 1,
													bgcolor: "rgba(0,0,0,0.04)",
													borderRadius: 0.5,
													whiteSpace: "pre-wrap",
													wordBreak: "break-word",
												}}
											>
												{typeof clientError.details === "string"
													? clientError.details
													: JSON.stringify(clientError.details, null, 2)}
											</Box>
										)}
									</Alert>
								)}

								{renderClientsTable()}
							</CardContent>
						</Card>
					</Grid>
				</Grid>
			</Box>
		</Page>
	);
}
